/**
 * Step 3's closing block: the §7216 consent tick and the two optional
 * letterhead fields that travel with it.
 *
 * Split out of `LetterDetailsStep` under `docs/FILE_SIZE_POLICY.md`. The
 * consent box is in the hard floor — no attestation and no document stands in
 * for it — so it stays a plain required field with a star, unaffected by the
 * evidence-tier work happening above it on the same step.
 */
import type { LetterFormPatch, LetterFormState } from '@/components/attest/formState'
import { EXAMPLE_PLACEHOLDER, RequiredMark } from '@/components/attest/requiredFields'
import { WIZARD_FIELD_HELP } from '@/components/attest/wizardHelp'
import { HelpDot } from '@/components/ui/help-dot'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

export interface LetterConsentSectionProps {
  form: LetterFormState
  onChange: (patch: LetterFormPatch) => void
}

export function LetterConsentSection({ form, onChange }: LetterConsentSectionProps) {
  return (
    <section className="rounded-lg border border-border p-4">
      {/* `id` matches the preflight blocker's, so "Correct here" can focus the
          box itself rather than the step (ATTEST-UX-2 D2). */}
      <div className="flex items-start gap-2">
        <label className="flex flex-1 items-start gap-3 text-sm" htmlFor="client-consent">
          <input
            id="client-consent"
            type="checkbox"
            data-testid="client-consent"
            className="mt-0.5 h-4 w-4"
            checked={form.clientConsentOnFile}
            onChange={e => onChange({ clientConsentOnFile: e.target.checked })}
          />
          {/* ATTEST-DEMO-POLISH-1 item 3 — copy only. The old wording read as
              a statement of fact ("consent ... is on file"), so it was easy to
              scan past without acting on it. Action-first now, and it still
              names §7216. Required-ness, the star, and the completion-counter
              wiring are unchanged. Final legal wording routes through Leonid. */}
          <span>
            <strong>Select to confirm — required:</strong>{' '}
            I have the client’s written consent on file to release this
            information (IRC §7216 applies where tax-return information is
            disclosed).
            <RequiredMark field="client-consent" />
          </span>
        </label>
        <HelpDot {...WIZARD_FIELD_HELP.clientConsent} testId="info-clientConsent" />
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="engagement-state">Engagement state (optional)</Label>
          <Input
            id="engagement-state"
            className={`mt-1 ${EXAMPLE_PLACEHOLDER}`}
            placeholder="e.g. TX"
            value={form.engagementState}
            onChange={e => onChange({ engagementState: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="issuer-address">Issuer address (optional)</Label>
          <Textarea
            id="issuer-address"
            rows={2}
            className={`mt-1 ${EXAMPLE_PLACEHOLDER}`}
            placeholder={'One line per address line'}
            value={form.issuerAddressText}
            onChange={e => onChange({ issuerAddressText: e.target.value })}
          />
        </div>
      </div>
    </section>
  )
}
